import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import api from "../../services/api";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Card = styled.div`
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--button-shadow);
  padding: 18px;
`;

const CardTitle = styled.h4`
  margin: 0 0 14px;
`;

const Note = styled.p`
  margin: 0 0 16px;
  opacity: 0.75;
  line-height: 1.45;
`;

const SummaryGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-bottom: 18px;
`;

const SummaryItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--app-bg);
`;

const Meta = styled.div`
  font-size: 13px;
  opacity: 0.75;
  line-height: 1.5;
`;

const CategoryGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 18px;
`;

const CategoryCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  background: linear-gradient(180deg, var(--app-bg), var(--button-bg));
`;

const CategoryTitle = styled.div`
  font-weight: 700;
`;

const PreviewGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const PreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow: hidden;
  background: var(--app-bg);
  box-shadow: var(--button-shadow);
`;

const PreviewCover = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  background: linear-gradient(135deg, #eadfcb 0%, #d8ebdd 100%);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const PreviewFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  padding: 16px;
  background: linear-gradient(135deg, #f4e3bb 0%, #b7d8c2 100%);
  color: #1d1d1d;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
`;

const PreviewBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
`;

const PreviewBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PreviewCategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  background: rgba(94, 123, 89, 0.14);
  font-size: 12px;
  font-weight: 700;
`;

const PreviewMutedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  background: rgba(0, 0, 0, 0.05);
  font-size: 12px;
  font-weight: 600;
`;

const PreviewStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  background: rgba(216, 87, 87, 0.14);
  color: #912d2d;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PreviewTitle = styled.h4`
  margin: 0;
  line-height: 1.3;
`;

const PreviewSummary = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  opacity: 0.82;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.02);
`;

const InfoLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.6;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
`;

const PreviewLink = styled.a`
  margin-top: auto;
  font-size: 13px;
  font-weight: 700;
  color: inherit;
`;

const EmptyState = styled.div`
  padding: 24px 0;
  opacity: 0.75;
`;

const getWebsiteHref = website => {
  if (!website) {
    return null;
  }
  return website.includes("://") ? website : `https://${website}`;
};

const getPhoto = item => item.main_photo || item.fallback_photo;

const textOrFallback = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (typeof value === "string" && value.trim() === "") {
    return fallback;
  }

  return value;
};

class Coffee extends React.Component {
  renderPreviewSection = payload => {
    const items = (payload && payload.items) || [];
    const categoryBreakdown = (payload && payload.category_breakdown) || [];

    return (
      <Card>
        <CardTitle>Coffee Preview</CardTitle>
        <Note>
          Quick scan of curated London coffee spots from the places database, with the best
          available photo plus the key details most useful for QA and content cleanup.
        </Note>

        <SummaryGrid>
          <SummaryItem>
            <strong>London Coffee Places</strong>
            <Meta>{payload && payload.total_count ? payload.total_count : 0} curated entries</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>With Main Photo</strong>
            <Meta>
              {payload && payload.with_main_photo_count ? payload.with_main_photo_count : 0} ready
              for app cards
            </Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>With Any Photo</strong>
            <Meta>{payload && payload.with_any_photo_count ? payload.with_any_photo_count : 0} visible</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>With Opening Hours</strong>
            <Meta>
              {payload && payload.with_opening_hours_count ? payload.with_opening_hours_count : 0}{" "}
              configured
            </Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>With Website</strong>
            <Meta>{payload && payload.with_website_count ? payload.with_website_count : 0} linked</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Temporarily Closed</strong>
            <Meta>
              {payload && payload.temporarily_closed_count ? payload.temporarily_closed_count : 0}{" "}
              flagged
            </Meta>
          </SummaryItem>
        </SummaryGrid>

        {categoryBreakdown.length ? (
          <CategoryGrid>
            {categoryBreakdown.map(item => (
              <CategoryCard key={item.category_id}>
                <CategoryTitle>{item.category_name}</CategoryTitle>
                <Meta>{item.place_count || 0} places</Meta>
              </CategoryCard>
            ))}
          </CategoryGrid>
        ) : null}

        {items.length ? (
          <PreviewGrid>
            {items.map(item => {
              const photo = getPhoto(item);
              const websiteHref = getWebsiteHref(item.website);

              return (
                <PreviewCard key={item.place_id}>
                  <PreviewCover>
                    {photo ? (
                      <PreviewImage
                        src={photo.photo_url || photo.thumbnail_url}
                        alt={item.name || "Coffee place"}
                      />
                    ) : (
                      <PreviewFallback>{item.name || "Coffee place"}</PreviewFallback>
                    )}
                  </PreviewCover>

                  <PreviewBody>
                    <PreviewBadgeRow>
                      <PreviewCategoryBadge>{item.category_name || "Coffee"}</PreviewCategoryBadge>
                      <PreviewMutedBadge>Place #{item.place_id}</PreviewMutedBadge>
                      {!item.main_photo && item.fallback_photo ? (
                        <PreviewMutedBadge>Fallback photo</PreviewMutedBadge>
                      ) : null}
                      {item.temporary_closed ? (
                        <PreviewStatusBadge>Temporarily Closed</PreviewStatusBadge>
                      ) : null}
                    </PreviewBadgeRow>

                    <div>
                      <PreviewTitle>{item.name || "Untitled place"}</PreviewTitle>
                    </div>

                    <PreviewSummary>
                      {textOrFallback(item.description, "No description added yet.")}
                    </PreviewSummary>

                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Address</InfoLabel>
                        <InfoValue>{textOrFallback(item.address, "Address missing")}</InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Hours</InfoLabel>
                        <InfoValue>
                          {textOrFallback(
                            item.opening_summary,
                            item.opening_days_count
                              ? `Configured for ${item.opening_days_count} days`
                              : "Hours missing"
                          )}
                        </InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Phone</InfoLabel>
                        <InfoValue>{textOrFallback(item.phone_number, "Phone missing")}</InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Location</InfoLabel>
                        <InfoValue>
                          {item.location
                            ? `${item.location.lat.toFixed(4)}, ${item.location.lng.toFixed(4)}`
                            : "Coordinates missing"}
                        </InfoValue>
                      </InfoCard>
                    </InfoGrid>

                    {websiteHref ? (
                      <PreviewLink href={websiteHref} target="_blank" rel="noreferrer">
                        Open {textOrFallback(item.website_domain, "website")}
                      </PreviewLink>
                    ) : (
                      <Meta>Website missing</Meta>
                    )}
                  </PreviewBody>
                </PreviewCard>
              );
            })}
          </PreviewGrid>
        ) : (
          <EmptyState>No London coffee places are available in the curated database yet.</EmptyState>
        )}
      </Card>
    );
  };

  render() {
    return (
      <Page title="Coffee">
        <Stack>
          <Async fetchProps={api.coffee.listPreviews}>
            {payload => this.renderPreviewSection(payload)}
          </Async>
        </Stack>
      </Page>
    );
  }
}

export default Coffee;
